import { parseBlockTree, serializeBlockTree } from '@shared/block-editor';
import type { BlockNode, BlockTree } from '@shared/block-renderer';

import { EDITORIAL_BLOCK_TYPES, type EditorialBlockType } from './blocks';

/**
 * The body's wire boundary — **one module, both directions**, and the single
 * place the storage-shape conflict of `./types.ts` is resolved.
 *
 * The console holds a block tree, because that is what the UI/UX pack draws and
 * what the shared editor and renderer are. `content__editorial_posts` stores
 * `body_html TEXT`, and **the owning side wins on the wire** — the same
 * resolution `features/pages/_shared/body-html.ts` already reached for the
 * informational body, and for the same reason: the reader's renderer is
 * `frontend-landing`'s `Post.Body.tsx`, which puts the stored string through the
 * one audited `dangerouslySetInnerHTML` in that repo. HTML is what a reader is
 * served, so HTML is what a writer must store.
 *
 * **This used to send `{ body: BlockTree }` and the service refused it**, with a
 * `422` naming `body.bodyHtml MISSING` and `body.body EXTRA_FORBIDDEN`. No
 * editorial post could be created from the console or from anything else, which
 * took the whole state machine, the counters, the public listing, the RSS feed
 * and the post's own engagement surface out of reach behind it.
 *
 * **The two blocks that had no legal tag now have one.** `quote` renders its
 * attribution as `<cite>` and `note` as `<aside>`, and neither was in
 * `EDITORIAL_BODY_ALLOWED_TAGS`. Mapping them onto tags that *were* listed would
 * change what a reader is told a passage is — an attribution is not prose and a
 * notice is not a quotation — so the allow-list took the two tags instead, which
 * is a configuration row and the right place for the decision. Both are inert,
 * take no attribute, and cannot carry a URL.
 *
 * **Nothing here authors markup on the operator's behalf.** The operator types
 * text into a block; this module escapes it and wraps it in the one tag its kind
 * declares. There is no raw-HTML input path on the surface, which is the property
 * the sanitiser on the other side exists to enforce and this side must not
 * undermine.
 */

/** The shape the wire carries for a post's body. */
export interface WireBody {
  bodyHtml?: string | null;
  /** Tolerated on read only, for a payload written before the projection landed. */
  body?: unknown;
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function text(block: BlockNode): string {
  return typeof block.data.text === 'string' ? block.data.text : '';
}

function items(block: BlockNode): string[] {
  return Array.isArray(block.data.items)
    ? block.data.items.filter((item): item is string => typeof item === 'string')
    : [];
}

function attribute(block: BlockNode, name: string): string {
  const value = block.data[name];
  return typeof value === 'string' ? value : '';
}

/**
 * One block, as the single HTML shape the reader's renderer draws for its kind.
 *
 * A block with nothing in it emits nothing: an empty `<p>` renders as a gap on
 * the public page and reads as a layout defect rather than as a paragraph the
 * writer meant to leave blank.
 */
function toHtml(block: BlockNode): string[] {
  switch (block.type as EditorialBlockType) {
    case 'p': {
      const value = text(block).trim();
      return value ? [`<p>${escapeHtml(value)}</p>`] : [];
    }
    case 'h3': {
      const value = text(block).trim();
      return value ? [`<h3>${escapeHtml(value)}</h3>`] : [];
    }
    case 'ul': {
      const rows = items(block)
        .filter((item) => item.trim().length > 0)
        .map((item) => `<li>${escapeHtml(item)}</li>`)
        .join('');
      return rows ? [`<ul>${rows}</ul>`] : [];
    }
    case 'quote': {
      const value = text(block).trim();
      if (!value) return [];
      const cite = attribute(block, 'cite').trim();
      const attribution = cite ? `<cite>${escapeHtml(cite)}</cite>` : '';
      return [`<blockquote>${escapeHtml(value)}${attribution}</blockquote>`];
    }
    case 'note': {
      const value = text(block).trim();
      return value ? [`<aside>${escapeHtml(value)}</aside>`] : [];
    }
    case 'image': {
      const url = attribute(block, 'url').trim();
      const caption = attribute(block, 'caption').trim();
      // **A block with neither a source nor a caption emits nothing**, but one
      // with a caption survives: a post is written before its artwork lands, and
      // dropping the caption on save would delete text the operator typed and
      // could still see on screen.
      if (!url && !caption) return [];
      // The caption is the image's accessible name as well as its visible one,
      // so it is the `alt` rather than an empty string beside a `<figcaption>`.
      const figcaption = caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : '';
      const img = url ? `<img src="${escapeHtml(url)}" alt="${escapeHtml(caption)}"/>` : '';
      return [`<figure>${img}${figcaption}</figure>`];
    }
    case 'video': {
      const url = attribute(block, 'url').trim();
      const caption = attribute(block, 'caption').trim();
      if (!url && !caption) return [];
      const poster = attribute(block, 'poster').trim();
      const posterAttribute = poster ? ` poster="${escapeHtml(poster)}"` : '';
      const figcaption = caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : '';
      const video = url ? `<video src="${escapeHtml(url)}" controls${posterAttribute}></video>` : '';
      return [`<figure>${video}${figcaption}</figure>`];
    }
    default:
      // A type this build does not know is dropped from the MARKUP but survives
      // the round-trip through `fromWire` below, so an older tab never destroys
      // a body authored against a newer deploy.
      return [];
  }
}

/** The tree, as the HTML the owning service stores and the reader is served. */
export function toBodyHtml(tree: BlockTree): string {
  return tree.flatMap(toHtml).join('\n');
}

const KIND_FOR: Readonly<Record<string, EditorialBlockType>> = {
  P: 'p',
  H3: 'h3',
  UL: 'ul',
  BLOCKQUOTE: 'quote',
  ASIDE: 'note',
};

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * A block id, minted client-side because a block exists before any request
 * carries it. ULID-shaped — the platform's id form everywhere — with the
 * timestamp prefix that makes two blocks created in one sitting sort in the
 * order they were written.
 *
 * `crypto.getRandomValues` rather than `Math.random`: the id is a document key
 * and a collision inside one body silently merges two blocks on the next
 * keystroke.
 */
export function newBlockId(): string {
  let stamp = '';
  let time = Date.now();
  for (let index = 0; index < 10; index += 1) {
    stamp = CROCKFORD[time % 32] + stamp;
    time = Math.floor(time / 32);
  }

  const entropy = new Uint8Array(16);
  crypto.getRandomValues(entropy);

  let tail = '';
  for (const byte of entropy) tail += CROCKFORD[byte % 32];

  return stamp + tail;
}

function figureBlock(element: Element): BlockNode | null {
  const media = element.querySelector('img, video');
  const caption = element.querySelector('figcaption')?.textContent?.trim() ?? '';

  // A figure carrying only a caption is a media block authored before its file
  // landed. It reads back as an image with no source — the same state the editor
  // draws its placeholder for — rather than being dropped.
  if (!media) return caption ? { id: newBlockId(), type: 'image', data: { url: null, caption } } : null;

  const url = media.getAttribute('src') ?? '';

  if (media.tagName === 'IMG') {
    return { id: newBlockId(), type: 'image', data: { url, caption } };
  }

  return {
    id: newBlockId(),
    type: 'video',
    data: { url, caption, poster: media.getAttribute('poster') ?? null },
  };
}

function quoteBlock(element: Element): BlockNode {
  const cite = element.querySelector('cite');
  const attribution = cite?.textContent?.trim() ?? '';
  // Reading the attribution out first means the quotation's own text is what is
  // left, rather than the two running together in the editor.
  cite?.remove();

  return {
    id: newBlockId(),
    type: 'quote',
    data: { text: element.textContent?.trim() ?? '', cite: attribution },
  };
}

/**
 * The stored HTML, back into blocks.
 *
 * `DOMParser` builds an **inert** document — no script runs, no resource loads —
 * and only `textContent` and a fixed set of attributes are read out of it. No
 * value from this function ever reaches `dangerouslySetInnerHTML`; there is no
 * such call in this feature.
 */
export function fromBodyHtml(html: string | null | undefined): BlockTree {
  const source = (html ?? '').trim();
  if (!source) return [];

  const parsed = new DOMParser().parseFromString(source, 'text/html');
  const blocks: BlockNode[] = [];

  for (const node of Array.from(parsed.body.children)) {
    if (node.tagName === 'FIGURE') {
      const block = figureBlock(node);
      if (block) blocks.push(block);
      continue;
    }

    if (node.tagName === 'BLOCKQUOTE') {
      blocks.push(quoteBlock(node));
      continue;
    }

    if (node.tagName === 'UL') {
      blocks.push({
        id: newBlockId(),
        type: 'ul',
        data: {
          items: Array.from(node.children)
            .filter((row) => row.tagName === 'LI')
            .map((row) => row.textContent?.trim() ?? '')
            .filter((row) => row.length > 0),
        },
      });
      continue;
    }

    const kind = KIND_FOR[node.tagName];
    if (!kind) continue;

    blocks.push({ id: newBlockId(), type: kind, data: { text: node.textContent ?? '' } });
  }

  return blocks;
}

/**
 * The payload's body, as the editor holds it.
 *
 * A block tree on the payload still wins where one is present, so a post written
 * during the window when this console sent trees is not re-parsed out of markup
 * it never had. Everything else comes from `bodyHtml`.
 */
export function fromWire(payload: WireBody): BlockTree {
  const tree = parseBlockTree(payload.body);
  if (tree.length > 0) return tree;
  return fromBodyHtml(payload.bodyHtml);
}

/** What a write puts on the wire. The service names this key and no other. */
export function toWire(tree: BlockTree): { bodyHtml: string } {
  return { bodyHtml: toBodyHtml(serializeBlockTree(tree)) };
}

/**
 * True when the payload carries a body this console cannot edit.
 *
 * Now that markup round-trips, the uneditable case is markup whose structure
 * these seven kinds cannot hold — a table, a heading level the pack does not
 * draw — rather than «HTML at all». The surfaces read this rather than assuming:
 * an empty canvas over a post that has prose is the defect this predicate exists
 * to make visible, and a save from that canvas would overwrite the writer's text
 * with nothing.
 */
export function isUneditableBody(payload: WireBody): boolean {
  if (Array.isArray(payload.body) && payload.body.length > 0) return false;

  const html = (payload.bodyHtml ?? '').trim();
  if (!html) return false;

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const elements = Array.from(parsed.body.children);
  const known = new Set([...Object.keys(KIND_FOR), 'FIGURE']);

  return elements.length > 0 && elements.some((node) => !known.has(node.tagName));
}

export { EDITORIAL_BLOCK_TYPES };
